<template>
  <div class="experts-view">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h4 font-weight-bold">Experts</h1>
      <div class="d-flex align-center">
        <v-btn
          class="create-expert-btn"
          variant="outlined"
          color="black"
          prepend-icon="mdi-plus"
          @click="showNewExpertModal = true"
        >
          New Expert
        </v-btn>
      </div>
    </div>

    <!-- Filter Controls -->
    <div class="filter-section d-flex align-center mb-6">
      <!-- Expert Qualification -->
      <div class="filter-group mr-6">
        <div class="text-subtitle-2 mb-2">Expert Qualification</div>
        <div class="star-rating-selector">
          <v-rating
            v-model="filters.qualification"
            color="#e94057"
            background-color="grey lighten-3"
            hover
            empty-icon="$ratingEmpty"
            full-icon="$ratingFull"
            half-icon="$ratingHalf"
          ></v-rating>
        </div>
      </div>

      <!-- Expertise Segmentation -->
      <div class="filter-group mr-6">
        <div class="text-subtitle-2 mb-2">Expertise Segentation</div>
        <v-select
          v-model="filters.expertiseSegmentation"
          :items="expertiseSegmentationOptions"
          outlined
          dense
          hide-details
          class="filter-select rounded-lg"
        ></v-select>
      </div>

      <!-- Gender -->
      <div class="filter-group mr-6">
        <div class="text-subtitle-2 mb-2">Gender</div>
        <v-select
          v-model="filters.gender"
          :items="genderOptions"
          outlined
          dense
          hide-details
          class="filter-select rounded-lg"
        ></v-select>
      </div>

      <v-spacer></v-spacer>

      <!-- Search and Filters -->
      <div class="filter-actions d-flex align-center">
        <div class="d-flex align-center gap-6 expert-stats-container mr-4">
          <div class="expert-stat">
            <div class="stat-label">Total</div>
            <div class="stat-value">{{ experts.length }}</div>
          </div>
          <div class="expert-stat">
            <div class="stat-label">Coke</div>
            <div class="stat-value">{{ expertsWorkingForCoke }}</div>
          </div>
        </div>
        <!-- Reset Filters Button -->
        <v-btn
          outlined
          rounded
          class="filters-btn mr-2 reset-filters-btn"
          @click="resetFilters"
        >
          <v-icon color="#4a4a4a">mdi-filter-remove</v-icon>
        </v-btn>
        <v-btn 
          outlined
          rounded
          class="filters-btn px-3" 
          @click="showFiltersModal = true"
        >
          <v-icon left small>mdi-filter-variant</v-icon>
          Filters
        </v-btn>
        <!-- Export CSV Button -->
        <v-btn
          outlined
          rounded
          class="filters-btn px-3 ml-2"
          @click="exportExperts"
        >
          <v-icon left small>mdi-download</v-icon>
          Export
        </v-btn>
      </div>
    </div>

    <!-- Experts Table -->
    <v-card class="experts-table-card mb-4 elevation-0">
      <!-- Skeleton Loader -->
      <div v-if="loading" class="skeleton-table">
        <!-- Skeleton Table Header -->
        <div class="skeleton-header">
          <div v-for="header in headers" :key="header.value" class="skeleton-header-cell" :style="{ width: header.width || 'auto' }">
            <div v-if="header.text" class="skeleton-box skeleton-text" style="width: 60%;"></div>
          </div>
        </div>
        
        <!-- Skeleton Table Rows -->
        <div v-for="i in 8" :key="i" class="skeleton-row">
          <!-- Photo Column -->
          <div class="skeleton-cell" style="width: 60px; text-align: center;">
            <div class="skeleton-box skeleton-avatar"></div>
          </div>
          <!-- Name Column -->
          <div class="skeleton-cell" style="flex: 1;">
            <div class="skeleton-box skeleton-text" style="width: 70%;"></div>
          </div>
          <!-- Company Column -->
          <div class="skeleton-cell" style="flex: 1;">
            <div class="skeleton-box skeleton-text" style="width: 60%;"></div>
          </div>
          <!-- Member Since Column -->
          <div class="skeleton-cell" style="flex: 1;">
            <div class="skeleton-box skeleton-text" style="width: 65%;"></div>
          </div>
          <!-- Activity Column -->
          <div class="skeleton-cell" style="flex: 1;">
            <div class="skeleton-activity">
              <div class="skeleton-box skeleton-chip"></div>
              <div class="skeleton-box skeleton-chip ml-2"></div>
            </div>
          </div>
          <!-- Rating Column -->
          <div class="skeleton-cell" style="flex: 1;">
            <div class="skeleton-rating">
              <div v-for="n in 5" :key="n" class="skeleton-box skeleton-star"></div>
            </div>
          </div>
          <!-- Actions Column -->
          <div class="skeleton-cell" style="text-align: right;">
            <div class="skeleton-actions">
              <div class="skeleton-box skeleton-button"></div>
              <div class="skeleton-box skeleton-button"></div>
            </div>
          </div>
        </div>
      </div>
      
      <DataTable
        v-else
        :headers="headers"
        :items="experts"
        :loading="loading"
        :show-pagination="false"
        class="experts-table"
      >
        <!-- Photo Column -->
        <template #column-photo="{ item }">
          <v-avatar
            v-if="item.photoUrl"
            size="40"
            class="expert-avatar"
          >
            <v-img
              :src="item.photoUrl"
              :alt="`${item.name} profile picture`"
              class="rounded-circle"
            ></v-img>
          </v-avatar>
          <v-avatar
            v-else
            size="40"
            class="expert-avatar avatar-placeholder"
          >
            {{ getExpertInitials(item) }}
          </v-avatar>
        </template>

        <!-- Name Column -->
        <template #column-name="{ item }">
          <div 
            class="expert-name-cell" 
            @click="viewExpertProfile(item)"
          >
            {{ item.name }}
          </div>
        </template>

        <!-- Company Column -->
        <template #column-currentCompany="{ item }">
          <div>
            {{ item.currentCompany || '-' }}
          </div>
        </template>

        <!-- Activity Column -->
        <template #column-activity="{ item }">
          <v-chip
            small
            :color="getActivityColor(item.activity)"
            text-color="white"
            class="status-chip"
            label
          >
            {{ item.activity }}
          </v-chip>
          <v-chip
            small
            :color="item.registrationComplete ? 'green' : 'grey'"
            text-color="white"
            class="status-chip ml-2"
            label
          >
            {{ item.registrationComplete ? 'Registered' : 'Not Registered yet' }}
          </v-chip>
        </template>

        <!-- Rating Column -->
        <template #column-rating="{ item }">
          <div class="d-flex">
            <v-icon
              v-for="n in 5"
              :key="n"
              :color="n <= item.rating ? '#e94057' : 'grey-lighten-2'"
              size="18"
            >
              {{ n <= item.rating ? 'mdi-star' : 'mdi-star-outline' }}
            </v-icon>
          </div>
        </template>

        <!-- Actions Column -->
        <template #column-actions="{ item }">
          <div class="d-flex justify-end gap-4">
            <v-menu v-if="!item.isAdmin" open-on-hover offset-y>
              <template #activator="{ props, on }">
                <ActionButton v-bind="props" v-on="on" variant="secondary">
                  Actions
                </ActionButton>
              </template>
              <v-list>
                <v-list-item @click="confirmDelete(item)">
                  <v-list-item-title>Delete Expert</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <ActionButton variant="primary" @click="viewExpertProfile(item)">
              {{ item.isAdmin ? 'See Admin' : 'See expert' }}
            </ActionButton>
          </div>
        </template>
      </DataTable>
    </v-card>

    <!-- Filters Modal -->
    <v-dialog
      v-model="showFiltersModal"
      max-width="600px"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center pa-4">
          <span class="text-h6">Filters</span>
          <v-btn icon @click="showFiltersModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pb-0">
          <div class="filter-modal-content">
            <!-- Gender Filters -->
            <FormSection title="Gender" class="mb-6">
              <RadioGroup
                v-model="advancedFilters.gender"
                name="gender"
                :options="[
                  { value: 'All', text: 'All' },
                  { value: 'Male', text: 'Male' },
                  { value: 'Female', text: 'Female' }
                ]"
              />
            </FormSection>

            <!-- Expertise segmentation level -->
            <FormSection title="Expertise segmentation" class="mb-6">
              <RadioGroup
                v-model="advancedFilters.expertiseLevel"
                name="expertiseLevel"
                :options="[
                  { value: 'All', text: 'All' },
                  { value: 'Beginner', text: 'Beginner' },
                  { value: 'Intermediate', text: 'Intermediate' },
                  { value: 'Expert', text: 'Expert' }
                ]"
              />
            </FormSection>

            <!-- Design Expertise -->
            <FormSection title="Design Expertise" class="mb-6">
              <RadioGroup
                v-model="advancedFilters.designExpertise"
                name="designExpertise"
                :options="[
                  { value: 'All', text: 'All' },
                  { value: 'Package design', text: 'Package design' },
                  { value: 'Product design', text: 'Product design' },
                  { value: 'Graphic design', text: 'Graphic design' }
                ]"
              />
            </FormSection>

            <!-- Expertise Segmentation (Industry) -->
            <FormSection title="Expertise Segmentation" class="mb-6">
              <div class="expertise-pills d-flex flex-wrap">
                <SelectionChip
                  v-for="(segment, index) in expertiseSegments"
                  :key="index"
                  :selected="isSegmentSelected(segment)"
                  @toggle="toggleSegment(segment)"
                  class="ma-1"
                >
                  {{ segment }}
                </SelectionChip>
              </div>
            </FormSection>

            <!-- Background -->
            <FormSection title="Background" class="mb-6">
              <div class="background-pills d-flex flex-wrap">
                <SelectionChip
                  v-for="(background, index) in backgroundOptions"
                  :key="index"
                  :selected="isBackgroundSelected(background)"
                  @toggle="toggleBackground(background)"
                  class="ma-1"
                >
                  {{ background }}
                </SelectionChip>
              </div>
            </FormSection>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <ActionButton
            variant="text"
            @click="resetFilters"
          >
            Reset
          </ActionButton>
          <ActionButton
            variant="primary"
            @click="applyFilters"
          >
            Apply Filters
          </ActionButton>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Modal -->
    <v-dialog
      v-model="showDeleteConfirmation"
      max-width="400px"
    >
      <v-card>
        <v-card-title class="text-h5">
          Confirm Deletion
        </v-card-title>
        
        <v-card-text>
          Are you sure you want to delete expert "{{ expertToDelete?.name }}"? This action cannot be undone.
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <ActionButton
            variant="secondary"
            @click="showDeleteConfirmation = false"
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="primary"
            color="error"
            @click="deleteExpert"
            :loading="deleteLoading"
          >
            Delete
          </ActionButton>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Expert Modal -->
    <v-dialog v-model="showNewExpertModal" max-width="500px" attach="body" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center pa-4">
          <span class="text-h6">New Expert</span>
          <v-btn icon @click="showNewExpertModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field label="First Name" v-model="newExpertFirstName" outlined dense />
          <v-text-field label="Last Name" v-model="newExpertLastName" outlined dense />
          <v-text-field label="Email" v-model="newExpertEmail" outlined dense />
          <v-text-field label="Email Title" v-model="newExpertEmailTitle" outlined dense />
          <v-textarea label="Email Content" v-model="newExpertEmailContent" outlined dense rows="4" />
          <v-checkbox
            v-model="newExpertWorksForCoke"
            label="Works for Coke"
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <ActionButton variant="text" @click="showNewExpertModal = false">
            Cancel
          </ActionButton>
          <ActionButton variant="primary" @click="createExpert" :loading="createLoading">
            Create Expert
          </ActionButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import ActionButton from '@/components/common/ActionButton.vue';
import SelectionChip from '@/components/common/SelectionChip.vue';
import FormSection from '@/components/common/FormSection.vue';
import RadioGroup from '@/components/common/RadioGroup.vue';
import DataTable from '@/components/DataTable.vue';
import { useToast } from 'vue-toastification';

export default {
  name: 'ExpertsView',
  components: {
    ActionButton,
    SelectionChip,
    FormSection,
    RadioGroup,
    DataTable
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: false,
      // Pagination removed; we now fetch and show all experts
      searchQuery: '',
      showFiltersModal: false,
      
      // Basic filters
      filters: {
        qualification: 0,
        expertiseSegmentation: 'All',
        gender: 'All'
      },
      
      // Advanced filters
      advancedFilters: {
        gender: 'All',
        expertiseLevel: 'All',
        designExpertise: 'All',
        expertiseSegments: [],
        backgrounds: []
      },
      
      // Table headers
      headers: [
        { text: '', value: 'photo', align: 'center', sortable: false, width: '60px' },
        { text: 'Name', value: 'name', align: 'start' },
        { text: 'Company', value: 'currentCompany' },
        { text: 'Member since', value: 'memberSince' },
        { text: 'Activity', value: 'activity' },
        { text: 'Rating', value: 'rating' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ],
      
      // Demo data - would normally be fetched from API
      experts: [],
      
      // Options for dropdowns
      expertiseSegmentationOptions: ['All', 'Beginner', 'Intermediate', 'Expert'],
      genderOptions: ['All', 'Male', 'Female'],
      
      // Options for advanced filters
      expertiseSegments: [
        'CPG', 'Food & Beverage', 'Retail', 'Marketing and Branding', 'Sustainability',
        'Supply Chain and Manufacturing', 'Consumer psychology and behavioral experts',
        'Global Brand Expertise', 'Cultural Understanding'
      ],
      backgroundOptions: [
        'Agency', 'Design', 'Academia', 'Marketing', 'Market/ Consumer Research'
      ],
      
      showDeleteConfirmation: false,
      expertToDelete: null,
      deleteLoading: false,
      createLoading: false,
      showNewExpertModal: false,
      newExpertFirstName: '',
      newExpertLastName: '',
      newExpertEmail: '',
      newExpertEmailTitle: 'Invitation to Expert Panel',
      newExpertEmailContent: 'Hello [username],\n\nYou are invited to join our Expert Panel. Please complete your profile.',
      newExpertWorksForCoke: false
    };
  },
  
  mounted() {
    this.fetchFilterOptions();
    this.fetchExperts();
  },
  
  methods: {
    fetchExperts() {
      this.loading = true;
      
      // Debug logging to check auth token
      console.log('Auth token in fetchExperts:', localStorage.getItem('authToken'));
      
      // Build the query parameters for filtering
      const params = new URLSearchParams();
      
      // Add search query if it exists
      if (this.searchQuery) {
        params.append('search', this.searchQuery);
      }
      
      // Add qualification filter if it's set
      if (this.filters.qualification > 0) {
        params.append('qualification', this.filters.qualification);
      }
      
      // Add expertise level filter if it's not "All"
      if (this.filters.expertiseSegmentation !== 'All') {
        params.append('expertise_level', this.filters.expertiseSegmentation);
      }
      
      // Add gender filter if it's not "All"
      if (this.filters.gender !== 'All') {
        params.append('gender', this.filters.gender);
      }
      
      // Add advanced filters if they're set
      if (this.advancedFilters.designExpertise !== 'All') {
        params.append('design_expertise', this.advancedFilters.designExpertise);
      }
      
      // Add expertise segments (multiple can be selected)
      this.advancedFilters.expertiseSegments.forEach(segment => {
        params.append('segments', segment);
      });
      
      // Add backgrounds (multiple can be selected)
      this.advancedFilters.backgrounds.forEach(background => {
        params.append('backgrounds', background);
      });
      
      // Pagination removed: do not send page or page_size
      
      // Make the API request
      apiService.get(`experts/list/?${params.toString()}`)
        .then(response => {
          console.log('Experts API response:', response.data);
          // With pagination disabled, backend returns a plain list
          this.experts = Array.isArray(response.data) ? response.data : (response.data.results || []);
          console.log('Experts with ratings:', this.experts.map(e => ({ name: e.name, rating: e.rating })));
        })
        .catch(error => {
          console.error('Error fetching experts:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    fetchFilterOptions() {
      apiService.get('experts/filter-options/')
        .then(response => {
          // Update the filter options with data from the backend
          this.expertiseSegmentationOptions = response.data.expertiseLevels;
          this.genderOptions = response.data.genders;
          this.expertiseSegments = response.data.expertiseSegments;
          this.backgroundOptions = response.data.backgrounds;
        })
        .catch(error => {
          console.error('Error fetching filter options:', error);
        });
    },
    
    // Pagination removed
    
    onSearch() {
      this.fetchExperts();
    },
    
    getActivityColor(activity) {
      switch (activity) {
        case 'Active':
          return 'green';
        case 'Inactive':
          return 'grey';
        case 'Pending':
          return 'orange';
        default:
          return 'grey';
      }
    },
    
    viewExpertProfile(expert) {
      this.$router.push(`/experts/${expert.id}`);
    },
    
    showExpertActions(expert) {
      // Implement actions menu
      console.log('Show actions for expert:', expert);
    },
    
    openExpertsSummary() {
      // Navigate to experts summary page
      console.log('Navigate to experts summary');
    },
    
    exportExperts() {
      // Build query params identical to fetchExperts to apply current filters
      const params = new URLSearchParams();

      if (this.searchQuery) {
        params.append('search', this.searchQuery);
      }

      if (this.filters.qualification > 0) {
        params.append('qualification', this.filters.qualification);
      }

      if (this.filters.expertiseSegmentation !== 'All') {
        params.append('expertise_level', this.filters.expertiseSegmentation);
      }

      if (this.filters.gender !== 'All') {
        params.append('gender', this.filters.gender);
      }

      if (this.advancedFilters.designExpertise !== 'All') {
        params.append('design_expertise', this.advancedFilters.designExpertise);
      }

      this.advancedFilters.expertiseSegments.forEach(segment => {
        params.append('segments', segment);
      });

      this.advancedFilters.backgrounds.forEach(background => {
        params.append('backgrounds', background);
      });

      // Request CSV blob from backend
      apiService.get(`experts/export/?${params.toString()}`, { responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'experts.csv');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.error('Error exporting experts:', error);
          this.toast.error('Failed to export experts');
        });
    },
    
    // Filter modal methods
    isSegmentSelected(segment) {
      return this.advancedFilters.expertiseSegments.includes(segment);
    },
    
    toggleSegment(segment) {
      const index = this.advancedFilters.expertiseSegments.indexOf(segment);
      if (index === -1) {
        this.advancedFilters.expertiseSegments.push(segment);
      } else {
        this.advancedFilters.expertiseSegments.splice(index, 1);
      }
    },
    
    isBackgroundSelected(background) {
      return this.advancedFilters.backgrounds.includes(background);
    },
    
    toggleBackground(background) {
      const index = this.advancedFilters.backgrounds.indexOf(background);
      if (index === -1) {
        this.advancedFilters.backgrounds.push(background);
      } else {
        this.advancedFilters.backgrounds.splice(index, 1);
      }
    },
    
    resetFilters() {
      // Reset basic filters
      this.filters = {
        qualification: 0,
        expertiseSegmentation: 'All',
        gender: 'All'
      };

      // Reset advanced filters
      this.advancedFilters = {
        gender: 'All',
        expertiseLevel: 'All',
        designExpertise: 'All',
        expertiseSegments: [],
        backgrounds: []
      };

      // Clear search query
      this.searchQuery = '';

      // Fetch experts with cleared filters
      this.fetchExperts();
    },
    
    applyFilters() {
      this.showFiltersModal = false;
      this.fetchExperts();
    },
    
    confirmDelete(expert) {
      this.expertToDelete = expert;
      this.showDeleteConfirmation = true;
    },
    
    deleteExpert() {
      if (!this.expertToDelete) return;
      
      this.deleteLoading = true;
      
      apiService.delete(`experts/${this.expertToDelete.id}/`)
        .then(() => {
          // Show success notification
          this.toast.success('Expert deleted successfully');
          
          // Remove from list
          const index = this.experts.findIndex(e => e.id === this.expertToDelete.id);
          if (index !== -1) {
            this.experts.splice(index, 1);
          }
          
          // Close modal
          this.showDeleteConfirmation = false;
          this.expertToDelete = null;
        })
        .catch(error => {
          console.error('Error deleting expert:', error);
          
          // Show error notification
          this.toast.error('Failed to delete expert');
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
    
    createExpert() {
      if (!this.newExpertFirstName || !this.newExpertLastName || !this.newExpertEmail) {
        this.toast.error('First name, last name, and email are required.');
        return;
      }

      const payload = {
        firstName: this.newExpertFirstName,
        lastName: this.newExpertLastName,
        email: this.newExpertEmail,
        emailTitle: this.newExpertEmailTitle,
        emailContent: this.newExpertEmailContent,
        worksForCoke: this.newExpertWorksForCoke
      };

      this.createLoading = true;
      apiService.post('experts/invite/', payload)
        .then(() => {
          this.toast.success('Expert invitation sent successfully!');
          // Optionally refresh experts list
          this.fetchExperts();
          this.showNewExpertModal = false;
          this.newExpertFirstName = '';
          this.newExpertLastName = '';
          this.newExpertEmail = '';
        })
        .catch(error => {
          console.error('Error sending invitation:', error);
          const message = error.response?.data?.detail || 'Failed to send invitation.';
          this.toast.error(message);
        })
        .finally(() => {
          this.createLoading = false;
          // Reset form fields regardless of outcome
          this.newExpertFirstName = '';
          this.newExpertLastName = '';
          this.newExpertEmail = '';
          this.newExpertWorksForCoke = false;
          this.newExpertEmailTitle = 'Invitation to Expert Panel';
          this.newExpertEmailContent = 'Hello [username],\n\nYou are invited to join our Expert Panel. Please complete your profile.';
        });
    },

    // Helper to compute initials when expert photo is not available
    getExpertInitials(expert) {
      if (!expert) return 'G';
      const first = expert.firstName || expert.first_name || '';
      const last = expert.lastName || expert.last_name || '';
      let initials = '';
      if (first) initials += first.charAt(0).toUpperCase();
      if (last) initials += last.charAt(0).toUpperCase();
      if (!initials && expert.name) {
        const parts = expert.name.trim().split(' ');
        if (parts.length === 1) {
          initials = parts[0].charAt(0).toUpperCase();
        } else {
          initials =
            parts[0].charAt(0).toUpperCase() +
            parts[parts.length - 1].charAt(0).toUpperCase();
        }
      }
      return initials || 'G';
    }
  },
  computed: {
    expertsWorkingForCoke() {
      return this.experts.filter(expert => expert.worksForCoke || expert.works_for_coke).length;
    }
  },
  watch: {
    filters: {
      handler() {
        this.fetchExperts();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.experts-view {
  padding: 24px;
  background-color: #f8f9fa;
}

.create-expert-btn {
  height: 40px;
  background-color: transparent !important;
  color: black;
  font-weight: bold;
  border-radius: 8px;
  border: 1px solid black;
}

.filter-group {
  min-width: auto;
}

.filter-select {
  width: 200px;
  border-radius: 25px;
}

.filters-btn {
  border-radius: 25px;
  height: 40px;
}

.experts-table-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.experts-table {
  width: 100%;
  padding: 0 16px;
}

.expert-name-cell {
  cursor: pointer;
  color: #e94057;
  font-weight: 500;
}

.expert-name-cell:hover {
  text-decoration: underline;
}

.expert-avatar {
  border: 2px solid #f0f0f0;
  transition: border-color 0.2s;
}

.expert-avatar:hover {
  border-color: #e94057;
}

.status-chip {
  font-size: 12px;
  height: 24px;
}

/* Placeholder style when expert has no photo */
.avatar-placeholder {
  background-color: #e94057;
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}
 
.rounded-lg {
  border-radius: 25px !important;
}

.actions-menu {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Modify table style to add gaps between rows */
.experts-table :deep(table) {
  background-color: #f8f9fa;
  border-collapse: separate;
  border-spacing: 8px;
}

.experts-table :deep(tr) {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;
}

.experts-table :deep(tr:hover) {
  background-color: #f8f9fa;
}

.experts-table :deep(th) {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.experts-table :deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Reduce padding for photo column specifically */
.experts-table :deep(td:first-child) {
  padding: 12px 8px 12px 16px;
}

/* Reduce left padding for name column to bring it closer to photo */
.experts-table :deep(td:nth-child(2)) {
  padding-left: 8px;
}

.expert-stats-container {
  margin-right: 16px;
}

.expert-stat {
  text-align: center;
  min-width: 80px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* Skeleton Loader Styles */
.skeleton-table {
  padding: 16px;
  background-color: #f8f9fa;
}

.skeleton-header {
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-header-cell {
  flex: 1;
  display: flex;
  align-items: center;
}

.skeleton-row {
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  align-items: center;
}

.skeleton-cell {
  display: flex;
  align-items: center;
}

.skeleton-activity {
  display: flex;
  align-items: center;
}

.skeleton-rating {
  display: flex;
  gap: 4px;
  align-items: center;
}

.skeleton-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.skeleton-box {
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 20%,
    #f0f0f0 40%,
    #e0e0e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin: 0 auto;
}

.skeleton-text {
  height: 16px;
}

.skeleton-chip {
  height: 24px;
  width: 80px;
  border-radius: 12px;
}

.skeleton-star {
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

.skeleton-button {
  height: 36px;
  width: 90px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style> 